import React from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Chip,
	InputAdornment,
	ListSubheader,
	MenuItem,
	Select,
	TextField,
	Checkbox,
	ListItemText,
	InputLabel,
	FormControl,
} from '@mui/material';
import { OkCancelButtons } from '../OkCancelButtons/OkCancelButtons';
import { Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { _ } from 'utils/libHelper';

const containsText = (text, searchText) => {
	return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
};
/**
 *
 * @param {object} param
 * 	- options in param must be in format of [{label: 'label', value: any}, ...]
 * 	- label in option is searchable field
 * @returns
 */
export function SearchableMultiSelect({
	values,
	options,
	handleConfirm,
	handleCancel,
	showOkCancelBtns = true,
	// handleClose = () => null,
	label = '',
	InputLabelProps = {},
	sxSelect = () => ({}),
	renderValueSx = () => ({}),
}) {
	const intl = useIntl();
	// const classes = useStylesSelectEdit();

	const [selectedValues, setSelectedValues] = React.useState([]);
	const [searchText, setSearchText] = React.useState('');

	const debouncedSetSearchText = _.debounce((val) => setSearchText(val), 300);

	const displayedOptions = React.useMemo(
		() => (!searchText ? options : options.filter((opt) => containsText(opt.label, searchText))),
		[searchText, options]
	);

	React.useEffect(() => {
		setSelectedValues(values);
	}, [values]);

	return (
		<Box
			sx={{
				width: `100%`,
				color: 'inherit',
				fontSize: 'inherit',
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box
				sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', flexDirection: 'column' }}
			>
				<FormControl fullWidth>
					{label && (
						<InputLabel id="searchable-multip-select-label" {...InputLabelProps}>
							{label}
						</InputLabel>
					)}
					<Select
						sx={(theme) => ({
							width: '100%',
							margin: (theme) => theme.spacing(0.5, 0),
							...sxSelect(theme),
						})}
						labelId="searchable-multip-select-label"
						label={label}
						multiple
						// displayEmpty={!label}
						value={selectedValues}
						renderValue={(selected) => {
							if (selected.length === 0) {
								return null;
							}
							return (
								<Box
									sx={(theme) => ({
										display: 'flex',
										flexWrap: 'wrap',
										gap: 0.5,
										...renderValueSx(theme),
									})}
								>
									{options
										.filter((opt) => selected.includes(opt.value))
										.map((opt) => (
											<Chip
												key={opt.value}
												label={opt.label}
												size="small"
												color="primary"
												sx={{ justifyContent: 'space-between' }}
												onMouseDown={(e) => {
													// stop "click" event being propagated to Select comp
													e.stopPropagation();
												}}
												deleteIcon={<DeleteIcon fontSize="small" />}
												onDelete={() => {
													setSelectedValues((prev) => prev.filter((value) => value !== opt.value));
													if (!showOkCancelBtns) {
														handleConfirm(selectedValues.filter((value) => value !== opt.value));
													}
												}}
											/>
										))}
								</Box>
							);
						}}
						onChange={(e) => {
							setSelectedValues(e.target.value);
						}}
						onClose={() => {
							setSearchText('');
							if (!showOkCancelBtns) handleConfirm(selectedValues);
						}}
						MenuProps={{ autoFocus: false }}
					>
						<ListSubheader>
							<TextField
								size="small"
								// Autofocus on textfield
								autoFocus
								placeholder={intl.formatMessage({ id: `GENERAL.SearchPlaceholder` })}
								fullWidth
								onChange={(e) => debouncedSetSearchText(e.target.value)}
								onKeyDown={(e) => {
									if (e.key !== 'Escape') {
										// Prevents autoselecting item while typing (default Select behaviour)
										e.stopPropagation();
									}
								}}
								slotProps={{
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
									},
								}}
							/>
						</ListSubheader>
						{displayedOptions.map((opt, idx) => (
							<MenuItem key={opt.value + '-' + idx} value={opt.value}>
								<Checkbox checked={selectedValues.includes(opt.value)} />
								<ListItemText primary={`${opt.label}`} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
			{showOkCancelBtns && (
				<OkCancelButtons
					handleConfirm={() => handleConfirm(selectedValues)}
					handleCancel={handleCancel}
				/>
			)}
		</Box>
	);
}

SearchableMultiSelect.propTypes = {
	values: PropTypes.array.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.any.isRequired,
		})
	).isRequired,
	handleConfirm: PropTypes.func.isRequired,
	handleCancel: PropTypes.func,
	showOkCancelBtns: PropTypes.bool,
	// handleClose: PropTypes.func,
	label: PropTypes.string,
	InputLabelProps: PropTypes.object,
	sxSelect: PropTypes.func,
	renderValueSx: PropTypes.func,
};
